import type { ImgHTMLAttributes, ReactNode } from "react";
import { forwardRef, useMemo, useState } from "react";

type ImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet" | "placeholder"> & {
  src: string | { src: string; width?: number; height?: number };
  alt?: string;
  width?: number | string;
  height?: number | string;
  fill?: boolean;
  loader?: (args: { src: string; width: number; quality?: number }) => string;
  quality?: number;
  priority?: boolean;
  loading?: "lazy" | "eager";
  placeholder?: "blur" | "empty" | `data:image/${string}`;
  blurDataURL?: string;
  unoptimized?: boolean;
  onLoadingComplete?: (img: HTMLImageElement) => void;
  children?: ReactNode;
};

const DEFAULT_WIDTH = 640;

/**
 * Drop-in replacement for `next/image`.
 *
 * Next.js optimised and resized remote images through its image server. This
 * shim deliberately does not reimplement that: it renders a plain `<img>` with
 * lazy loading, an explicit aspect ratio when `fill` or `width`/`height` are
 * given, and an `onError` fallback so a missing asset degrades to alt text
 * instead of a broken-image icon. `unoptimized` is accepted and ignored because
 * there is no optimisation step to skip.
 */
export const Image = forwardRef<HTMLImageElement, ImageProps>(function Image(
  {
    src,
    alt = "",
    width,
    height,
    fill,
    loader,
    quality,
    priority,
    loading,
    placeholder,
    blurDataURL,
    unoptimized,
    onLoadingComplete,
    style,
    className,
    onError,
    ...rest
  },
  ref,
) {
  void quality;
  void placeholder;
  void blurDataURL;
  void unoptimized;

  const resolved = useMemo(() => (typeof src === "string" ? src : src.src), [src]);
  const [failed, setFailed] = useState(false);

  const eager = priority === true || loading === "eager";

  const computedStyle: React.CSSProperties | undefined = fill
    ? {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        ...style,
      }
    : style;

  if (fill) {
    if (failed || !resolved) {
      return (
        <span
          role="img"
          aria-label={alt}
          className={`block h-full w-full bg-slate-100 dark:bg-slate-800 ${className ?? ""}`}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", ...style }}
        />
      );
    }

    return (
      <img
        ref={ref}
        src={loader ? loader({ src: resolved, width: DEFAULT_WIDTH }) : resolved}
        alt={alt}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={eager ? "high" : undefined}
        className={className}
        style={computedStyle}
        onError={(event) => {
          setFailed(true);
          onError?.(event);
        }}
        onLoad={(event) => onLoadingComplete?.(event.currentTarget)}
        {...rest}
      />
    );
  }

  if (failed || !resolved) {
    return (
      <span
        role="img"
        aria-label={alt}
        className={`bg-slate-100 dark:bg-slate-800 ${className ?? ""}`}
        style={{ display: "inline-block", width, height, ...style }}
      />
    );
  }

  return (
    <img
      ref={ref}
      src={loader ? loader({ src: resolved, width: Number(width) || DEFAULT_WIDTH }) : resolved}
      alt={alt}
      width={width}
      height={height}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={eager ? "high" : undefined}
      className={className}
      style={computedStyle}
      onError={(event) => {
        setFailed(true);
        onError?.(event);
      }}
      onLoad={(event) => onLoadingComplete?.(event.currentTarget)}
      {...rest}
    />
  );
});

export default Image;